import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Car, Plus, Trash, PencilSimple, GasPump, Wrench as WrenchIcon, Warning,
  Speedometer, CalendarBlank, X, CaretDown, CaretUp
} from '@phosphor-icons/react';

const VEHICLE_TYPES = ['Car', 'Bike', 'Scooter', 'Other'];

const TYPE_COLORS = { Car: '#FF6B35', Bike: '#3B82F6', Scooter: '#22C55E', Other: '#A855F7' };

export default function VehiclesPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'Car', registration: '', insurance_expiry: '', puc_expiry: '', service_due_date: '', service_due_km: 0, current_km: 0, notes: '' });
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [tab, setTab] = useState('fuel');
  const [fuelForm, setFuelForm] = useState({ date: new Date().toISOString().slice(0, 10), liters: '', cost: '', odometer: '', station: '' });
  const [serviceForm, setServiceForm] = useState({ date: new Date().toISOString().slice(0, 10), description: '', cost: '', odometer: '', provider: '' });
  const [fuelLogs, setFuelLogs] = useState([]);
  const [serviceLogs, setServiceLogs] = useState([]);
  const [showFuelForm, setShowFuelForm] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);

  const fetchVehicles = async () => {
    try {
      const res = await api.get('/vehicles');
      setVehicles(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchVehicles(); }, [api]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, service_due_km: Number(form.service_due_km), current_km: Number(form.current_km) };
      if (editing) {
        await api.put(`/vehicles/${editing}`, payload);
      } else {
        await api.post('/vehicles', payload);
      }
      closeForm();
      fetchVehicles();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/vehicles/${id}`); setSelectedVehicle(null); fetchVehicles(); } catch (e) { console.error(e); }
  };

  const openEdit = (v) => {
    setForm({ name: v.name, type: v.type, registration: v.registration || '', insurance_expiry: v.insurance_expiry || '', puc_expiry: v.puc_expiry || '', service_due_date: v.service_due_date || '', service_due_km: v.service_due_km || 0, current_km: v.current_km || 0, notes: v.notes || '' });
    setEditing(v.vehicle_id);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm({ name: '', type: 'Car', registration: '', insurance_expiry: '', puc_expiry: '', service_due_date: '', service_due_km: 0, current_km: 0, notes: '' });
  };

  const selectVehicle = async (v) => {
    setSelectedVehicle(v);
    setTab('fuel');
    try {
      const [fRes, sRes] = await Promise.all([
        api.get(`/vehicles/${v.vehicle_id}/fuel`),
        api.get(`/vehicles/${v.vehicle_id}/service`)
      ]);
      setFuelLogs(fRes.data);
      setServiceLogs(sRes.data);
    } catch (e) { console.error(e); }
  };

  const handleAddFuel = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/vehicles/${selectedVehicle.vehicle_id}/fuel`, {
        ...fuelForm, liters: Number(fuelForm.liters), cost: Number(fuelForm.cost), odometer: Number(fuelForm.odometer)
      });
      setShowFuelForm(false);
      setFuelForm({ date: new Date().toISOString().slice(0, 10), liters: '', cost: '', odometer: '', station: '' });
      selectVehicle(selectedVehicle);
      fetchVehicles();
    } catch (err) { console.error(err); }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/vehicles/${selectedVehicle.vehicle_id}/service`, {
        ...serviceForm, cost: Number(serviceForm.cost), odometer: Number(serviceForm.odometer || 0)
      });
      setShowServiceForm(false);
      setServiceForm({ date: new Date().toISOString().slice(0, 10), description: '', cost: '', odometer: '', provider: '' });
      selectVehicle(selectedVehicle);
      fetchVehicles();
    } catch (err) { console.error(err); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="vehicles-page" className="space-y-6 relative">
      <div className="glow-orb-teal -top-20 right-0 z-0" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-10">
        <div>
          <p className="overline">Transport</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">{t('vehicle_management')}</h1>
          <p className="text-sm text-neutral-500 mt-1">Manage vehicles, fuel and service records</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-vehicle-btn"
          className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center gap-2 w-fit">
          <Plus size={16} /> {t('add_vehicle')}
        </button>
      </div>

      {/* Vehicles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
        {vehicles.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <Car size={48} weight="duotone" className="mx-auto text-neutral-600 mb-4" />
            <p className="text-neutral-500 text-sm">No vehicles added yet</p>
          </div>
        ) : vehicles.map(v => {
          const color = TYPE_COLORS[v.type] || '#737373';
          const hasAlerts = v.alerts && v.alerts.length > 0;
          return (
            <div key={v.vehicle_id}
              className={`glass-card p-6 cursor-pointer transition-all duration-200 animate-fade-up ${selectedVehicle?.vehicle_id === v.vehicle_id ? 'border-[#FF6B35]/40 ring-1 ring-[#FF6B35]/20' : ''}`}
              onClick={() => selectVehicle(v)} data-testid={`vehicle-card-${v.vehicle_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: `${color}15` }}>
                    <Car size={20} weight="duotone" style={{ color }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{v.name}</p>
                    <p className="text-[11px] text-neutral-500">{v.type} {v.registration && `\u00B7 ${v.registration}`}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); openEdit(v); }} data-testid={`edit-vehicle-${v.vehicle_id}`}
                    className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-500 hover:text-white transition-colors">
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(v.vehicle_id); }} data-testid={`delete-vehicle-${v.vehicle_id}`}
                    className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-500 hover:text-red-400 transition-colors">
                    <Trash size={14} />
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-white/[0.02] rounded-xl px-3 py-2">
                  <p className="text-[10px] text-neutral-500 uppercase">Odometer</p>
                  <p className="text-sm font-semibold text-white font-mono">{(v.current_km || 0).toLocaleString()} km</p>
                </div>
                <div className="bg-white/[0.02] rounded-xl px-3 py-2">
                  <p className="text-[10px] text-neutral-500 uppercase">Fuel Logs</p>
                  <p className="text-sm font-semibold text-white">{v.fuel_logs?.length || 0}</p>
                </div>
              </div>

              {/* Alerts */}
              {hasAlerts && (
                <div className="space-y-1">
                  {v.alerts.map((alert, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px] text-amber-400 bg-amber-500/5 rounded-lg px-3 py-1.5">
                      <Warning size={12} weight="fill" /> {alert}
                    </div>
                  ))}
                </div>
              )}

              {!hasAlerts && v.insurance_expiry && (
                <p className="text-[11px] text-neutral-500">Insurance: {v.insurance_expiry} &middot; PUC: {v.puc_expiry || 'N/A'}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected Vehicle Detail Panel */}
      {selectedVehicle && (
        <div className="glass-card p-6 relative z-10" data-testid="vehicle-detail-panel">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading font-semibold text-white text-lg">{selectedVehicle.name} - Logs</h2>
            <div className="flex gap-1 bg-white/5 rounded-xl p-1">
              <button onClick={() => setTab('fuel')} data-testid="vehicle-tab-fuel"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'fuel' ? 'bg-[#141414] shadow-sm text-white' : 'text-neutral-500 hover:text-white'}`}>
                <GasPump size={16} className="inline mr-1" /> Fuel
              </button>
              <button onClick={() => setTab('service')} data-testid="vehicle-tab-service"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'service' ? 'bg-[#141414] shadow-sm text-white' : 'text-neutral-500 hover:text-white'}`}>
                <WrenchIcon size={16} className="inline mr-1" /> Service
              </button>
            </div>
          </div>

          {tab === 'fuel' && (
            <div>
              <div className="flex justify-end mb-4">
                <button onClick={() => setShowFuelForm(true)} data-testid="add-fuel-btn"
                  className="px-4 py-2 rounded-full border border-[#FF6B35]/30 text-xs font-medium text-[#FF6B35] hover:bg-[#FF6B35]/10 flex items-center gap-1 transition-colors">
                  <Plus size={14} /> Add Fuel Log
                </button>
              </div>
              {fuelLogs.length === 0 ? (
                <p className="text-neutral-500 text-sm text-center py-8">No fuel logs yet</p>
              ) : (
                <div className="space-y-2">
                  {fuelLogs.map(fl => (
                    <div key={fl.log_id} className="flex items-center justify-between bg-white/[0.02] rounded-xl px-4 py-3">
                      <div className="flex items-center gap-3">
                        <GasPump size={16} className="text-amber-400" />
                        <div>
                          <p className="text-sm text-white">{fl.liters}L &middot; Rs.{fl.cost?.toLocaleString('en-IN')}</p>
                          <p className="text-[11px] text-neutral-500">{fl.date} &middot; {fl.odometer?.toLocaleString()} km{fl.station ? ` \u00B7 ${fl.station}` : ''}</p>
                        </div>
                      </div>
                      <span className="text-xs text-neutral-400 font-mono">Rs.{(fl.cost / fl.liters).toFixed(1)}/L</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'service' && (
            <div>
              <div className="flex justify-end mb-4">
                <button onClick={() => setShowServiceForm(true)} data-testid="add-service-btn"
                  className="px-4 py-2 rounded-full border border-[#00E5FF]/30 text-xs font-medium text-[#00E5FF] hover:bg-[#00E5FF]/10 flex items-center gap-1 transition-colors">
                  <Plus size={14} /> Add Service Record
                </button>
              </div>
              {serviceLogs.length === 0 ? (
                <p className="text-neutral-500 text-sm text-center py-8">No service records yet</p>
              ) : (
                <div className="space-y-2">
                  {serviceLogs.map(sl => (
                    <div key={sl.log_id} className="flex items-center justify-between bg-white/[0.02] rounded-xl px-4 py-3">
                      <div className="flex items-center gap-3">
                        <WrenchIcon size={16} className="text-[#00E5FF]" />
                        <div>
                          <p className="text-sm text-white">{sl.description}</p>
                          <p className="text-[11px] text-neutral-500">{sl.date}{sl.odometer ? ` \u00B7 ${sl.odometer?.toLocaleString()} km` : ''}{sl.provider ? ` \u00B7 ${sl.provider}` : ''}</p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-white">Rs.{sl.cost?.toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Vehicle Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeForm}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">{editing ? 'Edit Vehicle' : t('add_vehicle')}</h3>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">{t('name')}</label>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                    data-testid="vehicle-name-input" placeholder="e.g., Swift Dzire"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">Type</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                    data-testid="vehicle-type-select"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50">
                    {VEHICLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="overline block mb-2">{t('registration')}</label>
                <input type="text" value={form.registration} onChange={e => setForm({ ...form, registration: e.target.value })}
                  data-testid="vehicle-reg-input" placeholder="e.g., GJ01AB1234"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">{t('insurance_expiry')}</label>
                  <input type="date" value={form.insurance_expiry} onChange={e => setForm({ ...form, insurance_expiry: e.target.value })}
                    data-testid="vehicle-insurance-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('puc_expiry')}</label>
                  <input type="date" value={form.puc_expiry} onChange={e => setForm({ ...form, puc_expiry: e.target.value })}
                    data-testid="vehicle-puc-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">Service Due Date</label>
                  <input type="date" value={form.service_due_date} onChange={e => setForm({ ...form, service_due_date: e.target.value })}
                    data-testid="vehicle-servicedate-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">Service Due KM</label>
                  <input type="number" value={form.service_due_km} onChange={e => setForm({ ...form, service_due_km: e.target.value })}
                    data-testid="vehicle-servicekm-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Current KM</label>
                <input type="number" value={form.current_km} onChange={e => setForm({ ...form, current_km: e.target.value })}
                  data-testid="vehicle-km-input"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">{t('notes')}</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                  data-testid="vehicle-notes-input" placeholder="Optional notes"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button type="submit" data-testid="save-vehicle-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">{t('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Fuel Log Modal */}
      {showFuelForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowFuelForm(false)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">Add Fuel Log</h3>
            <form onSubmit={handleAddFuel} className="space-y-4">
              <div>
                <label className="overline block mb-2">{t('date')}</label>
                <input type="date" value={fuelForm.date} onChange={e => setFuelForm({ ...fuelForm, date: e.target.value })}
                  data-testid="fuel-date-input"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">{t('liters')}</label>
                  <input type="number" step="0.1" value={fuelForm.liters} onChange={e => setFuelForm({ ...fuelForm, liters: e.target.value })} required
                    data-testid="fuel-liters-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('cost')} (Rs.)</label>
                  <input type="number" value={fuelForm.cost} onChange={e => setFuelForm({ ...fuelForm, cost: e.target.value })} required
                    data-testid="fuel-cost-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">{t('odometer')} (km)</label>
                <input type="number" value={fuelForm.odometer} onChange={e => setFuelForm({ ...fuelForm, odometer: e.target.value })} required
                  data-testid="fuel-odometer-input"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">{t('station')}</label>
                <input type="text" value={fuelForm.station} onChange={e => setFuelForm({ ...fuelForm, station: e.target.value })}
                  data-testid="fuel-station-input" placeholder="e.g., HP Petrol Pump"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowFuelForm(false)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button type="submit" data-testid="save-fuel-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">{t('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Service Record Modal */}
      {showServiceForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowServiceForm(false)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">Add Service Record</h3>
            <form onSubmit={handleAddService} className="space-y-4">
              <div>
                <label className="overline block mb-2">{t('description')}</label>
                <input type="text" value={serviceForm.description} onChange={e => setServiceForm({ ...serviceForm, description: e.target.value })} required
                  data-testid="service-desc-input" placeholder="e.g., Oil change + filter"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">{t('date')}</label>
                  <input type="date" value={serviceForm.date} onChange={e => setServiceForm({ ...serviceForm, date: e.target.value })}
                    data-testid="service-date-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('cost')} (Rs.)</label>
                  <input type="number" value={serviceForm.cost} onChange={e => setServiceForm({ ...serviceForm, cost: e.target.value })} required
                    data-testid="service-cost-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">{t('odometer')} (km)</label>
                <input type="number" value={serviceForm.odometer} onChange={e => setServiceForm({ ...serviceForm, odometer: e.target.value })}
                  data-testid="service-odometer-input"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">Provider</label>
                <input type="text" value={serviceForm.provider} onChange={e => setServiceForm({ ...serviceForm, provider: e.target.value })}
                  data-testid="service-provider-input" placeholder="e.g., Maruti Authorized"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowServiceForm(false)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button type="submit" data-testid="save-service-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">{t('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
